/**
 * OpenList 文件浏览器主组件 - 重构后的版本
 */

"use client"

import { Card, CardContent } from "@/components/ui/card"
import { Loader2 } from "lucide-react"
import { useMobile } from "@/hooks/use-mobile"
import { FilePreviewDrawer } from "@/components/filePreview/file-preview-drawer"
import { FilePreviewModal } from "@/components/filePreview/file-preview-modal"

// 拆分后的组件
import { OpenListHeader } from "./openlist/openlist-header"
import { OpenListSearchResults } from "./openlist/openlist-search-results"
import { OpenListTreeView } from "./openlist/openlist-tree-view"
import { OpenListErrorState } from "./openlist/openlist-error-state"

// 自定义 Hook
import { useOpenListTree } from "@/hooks/use-openlist-tree"
import { useOpenListPreview } from "@/hooks/use-openlist-preview"
import { useOpenListSearch } from "@/hooks/use-openlist-search"

interface OpenListFolderExplorerProps {
  subjectId: string
  subjectName: string
  initialPath?: string
}

export function OpenListFolderExplorer({
  subjectId,
  subjectName,
  initialPath = "/",
}: OpenListFolderExplorerProps) {
  const isMobile = useMobile()

  // 使用自定义 Hook 管理状态
  const { treeNodes, expandedFolders, loadingNodes, error, setError, toggleFolder } =
    useOpenListTree({ subjectId, initialPath })

  const { clickedFileId, previewFile, isDownloading, handlePreview, handleDownload, closePreview } =
    useOpenListPreview({ subjectId })

  const { searchTerm, setSearchTerm, searchResults, isSearching, filteredTreeNodes, handleSearch } =
    useOpenListSearch({ subjectId, treeNodes, onError: setError })

  return (
    <Card className="w-full">
      <OpenListHeader
        subjectName={subjectName}
        searchTerm={searchTerm}
        isSearching={isSearching}
        onSearchTermChange={setSearchTerm}
        onSearch={handleSearch}
      />

      <CardContent>
        {/* 错误提示 */}
        {error && <OpenListErrorState error={error} onDismiss={() => setError(null)} />}

        {/* 初始加载状态 */}
        {loadingNodes.has("/") && treeNodes.length === 0 && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span>加载中...</span>
          </div>
        )}

        {/* 搜索结果 */}
        <OpenListSearchResults
          searchResults={searchResults}
          isDownloading={isDownloading}
          onPreview={handlePreview}
          onDownload={handleDownload}
        />

        {/* 树形文件结构 */}
        {!loadingNodes.has("/") &&
          treeNodes.length > 0 &&
          (!searchTerm || !searchResults.length) && (
            <OpenListTreeView
              treeNodes={filteredTreeNodes}
              expandedFolders={expandedFolders}
              loadingNodes={loadingNodes}
              clickedFileId={clickedFileId}
              isDownloading={isDownloading}
              onToggleFolder={toggleFolder}
              onPreview={handlePreview}
              onDownload={handleDownload}
            />
          )}

        {/* 空状态 */}
        {!loadingNodes.has("/") &&
          !error &&
          treeNodes.length === 0 &&
          (!searchTerm || !searchResults.length) && (
            <OpenListTreeView
              treeNodes={[]}
              expandedFolders={expandedFolders}
              loadingNodes={loadingNodes}
              clickedFileId={clickedFileId}
              isDownloading={isDownloading}
              onToggleFolder={toggleFolder}
              onPreview={handlePreview}
              onDownload={handleDownload}
            />
          )}
      </CardContent>

      {/* 预览组件 */}
      {isMobile ? (
        <FilePreviewDrawer file={previewFile} isOpen={!!previewFile} onClose={closePreview} />
      ) : (
        <FilePreviewModal file={previewFile} isOpen={!!previewFile} onClose={closePreview} />
      )}
    </Card>
  )
}
